import {
  DataGrid as FluentDataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  Text,
  createTableColumn,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import type { TableColumnDefinition, TableRowId } from '@fluentui/react-components';
import type { ReactNode } from 'react';

import { MOBILE, SHRINKABLE } from '../../responsive';

/** Declarative column for the generic resource table. */
export interface DataColumn<T> {
  /** Stable column id. */
  columnId: string;
  /** Header content. */
  header: ReactNode;
  /** Cell content for a given row. */
  renderCell: (item: T) => ReactNode;
  /**
   * Preferred column width in px.
   *
   * Without one, Fluent hands every column an equal share, and a cell whose
   * content varies in length then renders straight across its neighbour — on the
   * Employees roster, long emails were overlapping the Active checkbox. Set this
   * on the columns whose content varies; columns that omit it keep the default
   * even distribution.
   */
  width?: number;
  /** Floor for {@link width} when space is tight. Defaults to `width`. */
  minWidth?: number;
}

export interface DataTableProps<T> {
  /** Column definitions, in display order. */
  columns: ReadonlyArray<DataColumn<T>>;
  /** Row data. */
  rows: readonly T[];
  /** Stable, unique id per row (used as the React key and row id). */
  getRowId: (item: T) => string;
  /** Invoked when a row is activated (click / Enter). */
  onRowClick?: (item: T) => void;
  /** Message shown when there are no rows. */
  emptyMessage?: string;
  /**
   * Optional per-row extra class (merged after the clickable-row class). Lets a
   * caller decorate specific rows — e.g. group separators or a "continuous
   * shift" accent — without the generic table needing to know why. Return
   * `undefined` for no extra styling.
   */
  rowClassName?: (item: T) => string | undefined;
  /**
   * When true, an EMPTY table still renders its column headers (with the empty
   * message beneath) instead of collapsing to the message alone.
   *
   * Off by default so the existing sessions/employees/kiosks surfaces keep their
   * current look. My Reports opts in: there the columns ARE the content until a
   * report has data, and keeping the header mounted is what makes switching
   * reports read as one table reshaping rather than a panel swap.
   */
  showHeaderWhenEmpty?: boolean;
}

const useStyles = makeStyles({
  /**
   * The horizontal scroller for EVERY table in the app.
   *
   * It lives here rather than in each caller because it was in almost none of
   * them: a DataGrid sized by its widest cell pushed the whole PAGE wide on a
   * phone instead of scrolling, and the columns off the right edge were simply
   * unreachable (Michael, 2026-09-10). A table stays tabular on a phone — the
   * columns only mean anything next to each other — so it scrolls sideways
   * inside its own box.
   *
   * SHRINKABLE is what makes the `overflowX` actually work: without
   * `min-width: 0` this box refuses to become narrower than the grid inside it,
   * so there is nothing to scroll and the overflow lands on the document. See
   * responsive.ts.
   */
  scroll: {
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    ...SHRINKABLE,
    // On a phone the CARD list below replaces the grid entirely.
    [MOBILE]: { display: 'none' },
  },
  /**
   * MOBILE: one card per row instead of a table.
   *
   * A table squeezed into 390px does not merely get tight — the columns COLLIDE.
   * "Chris_Williams_Ph" printed on top of "Transportation", and a header read
   * "Departmen"+"Pay period" overlapping (Michael, 2026-09-10). Narrowing columns
   * or adding a sideways scrollbar only moves that around; the honest fix is to
   * stop pretending a 5-column grid fits, and stack each record as
   * label-over-value so nothing overlaps and nothing needs scrolling to reach.
   *
   * Driven purely by CSS (both trees render, one is hidden) rather than a JS
   * width check: no resize listener, no flash of the wrong layout, and the
   * breakpoint stays the shared one.
   */
  cards: {
    display: 'none',
    [MOBILE]: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalS,
      ...SHRINKABLE,
    },
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    ...SHRINKABLE,
  },
  cardField: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: tokens.spacingHorizontalM,
    ...SHRINKABLE,
  },
  cardLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    // The label is the narrow half; the value gets the room.
    flexShrink: 0,
  },
  cardValue: {
    textAlign: 'right',
    // Long names and departments WRAP here instead of running into the next
    // column, because on a card there is no next column.
    overflowWrap: 'anywhere',
    ...SHRINKABLE,
  },
  clickableRow: {
    cursor: 'pointer',
  },
  empty: {
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
});

/**
 * Generic Fluent {@link FluentDataGrid} resource table used by the supervisor
 * time surfaces (sessions / employees / kiosks). Mirrors the light-theme table
 * style used across the platform console but lives local to web-app (that
 * component is not a cross-package export).
 */
export function DataTable<T>({
  columns,
  rows,
  getRowId,
  onRowClick,
  emptyMessage = 'No items to display.',
  rowClassName,
  showHeaderWhenEmpty = false,
}: DataTableProps<T>): JSX.Element {
  const styles = useStyles();

  const columnDefs: Array<TableColumnDefinition<T>> = columns.map((col) =>
    createTableColumn<T>({
      columnId: col.columnId,
      renderHeaderCell: () => col.header,
      renderCell: (item) => col.renderCell(item),
    })
  );

  // Fluent only honours per-column sizing when the grid is told to size columns
  // at all, so this is passed only when at least one column declares a width —
  // otherwise every existing table would switch to fixed layout uninvited.
  const sizingOptions: Record<string, { defaultWidth: number; minWidth: number }> = {};
  for (const col of columns) {
    if (col.width !== undefined) {
      sizingOptions[col.columnId] = {
        defaultWidth: col.width,
        minWidth: col.minWidth ?? col.width,
      };
    }
  }
  const sized = Object.keys(sizingOptions).length > 0;
  const sizingProps = sized ? { resizableColumns: true, columnSizingOptions: sizingOptions } : {};

  if (rows.length === 0) {
    if (!showHeaderWhenEmpty) {
      return (
        <div className={styles.empty}>
          <Text>{emptyMessage}</Text>
        </div>
      );
    }
    return (
      <>
        {/* Same scroller as the populated path — a header-only grid is just as
            capable of pushing the page wide on a phone. */}
        <div className={styles.scroll}>
          <FluentDataGrid items={[]} columns={columnDefs} focusMode="row_unstable" size="medium">
            <DataGridHeader>
              <DataGridRow>
                {({ renderHeaderCell }): JSX.Element => (
                  <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                )}
              </DataGridRow>
            </DataGridHeader>
          </FluentDataGrid>
        </div>
        <div className={styles.empty}>
          <Text>{emptyMessage}</Text>
        </div>
      </>
    );
  }

  const resolveRowId = (item: T): TableRowId => getRowId(item);

  /**
   * Phone layout: each row as a card of label/value pairs. Rendered ALONGSIDE the
   * grid, with CSS choosing which is visible — see the `cards` style.
   */
  const cardList = (
    <div className={styles.cards}>
      {rows.map((item) => (
        <div
          key={getRowId(item)}
          className={mergeClasses(
            styles.card,
            onRowClick !== undefined ? styles.clickableRow : undefined,
            rowClassName?.(item)
          )}
          onClick={onRowClick === undefined ? undefined : () => onRowClick(item)}
        >
          {columns.map((col) => (
            <div key={col.columnId} className={styles.cardField}>
              <span className={styles.cardLabel}>{col.header}</span>
              <span className={styles.cardValue}>{col.renderCell(item)}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <>
    {cardList}
    <div className={styles.scroll}>
      <FluentDataGrid
        items={rows as T[]}
        columns={columnDefs}
        getRowId={resolveRowId}
        focusMode="row_unstable"
        size="medium"
        {...sizingProps}
      >
        <DataGridHeader>
          <DataGridRow>
            {({ renderHeaderCell }): JSX.Element => (
              <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
            )}
          </DataGridRow>
        </DataGridHeader>
        <DataGridBody<T>>
          {({ item, rowId }): JSX.Element => (
            <DataGridRow<T>
              key={rowId}
              className={mergeClasses(
                onRowClick !== undefined ? styles.clickableRow : undefined,
                rowClassName?.(item)
              )}
              onClick={onRowClick === undefined ? undefined : () => onRowClick(item)}
            >
              {({ renderCell }): JSX.Element => <DataGridCell>{renderCell(item)}</DataGridCell>}
            </DataGridRow>
          )}
        </DataGridBody>
      </FluentDataGrid>
    </div>
    </>
  );
}
